"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiRefreshCw,
  FiUser,
  FiCheckSquare,
  FiPlus,
  FiUpload,
  FiTrash2,
  FiEdit
} from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import { Link } from "react-router-dom"
import { Pagination } from "../../../utils/pagination.utils"
import { ExcelDownload } from "../../../utils/excel.utils"
import { formatDate } from "../../activities/edit"
import axiosInstance from "../../../libs/axios"
import { FaClock, FaCheckCircle, FaTimesCircle, FaFileExcel, FaExclamationCircle, FaCalendarAlt, FaSort, FaSortUp, FaSortDown, FaRupeeSign } from "react-icons/fa"
import { useAuth } from "../../../provider/auth.provider"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import toast from "react-hot-toast"

import { useUrlFilters } from "../../../hooks/useUrlFilters";

import { useDebouncedCallback } from "../../../hooks/useDebounce"

/** Query params a dashboard tile may pre-apply to this list. */
export const URL_FILTER_KEYS = ["status", "status_list", "user_id", "project_id", "date_from", "date_to", "search"];

interface GeneralClaim {
  id: number
  staff_id: number
  staff_type: string
  association_type: string
  claim_title: string
  claim_description: string
  claim_date: string
  claim_category?: string
  travel_amount?: number
  da_amount?: number
  stay_amount?: number
  food_amount?: number
  other_amount1?: number
  other_description1?: string
  other_amount2?: number
  other_description2?: string
  submitted_total?: number
  approved_travel_amount?: number
  approved_da_amount?: number
  approved_stay_amount?: number
  approved_food_amount?: number
  approved_other_amount1?: number
  approved_other_description1?: string
  approved_other_amount2?: number
  approved_other_description2?: string
  final_approved_total?: number
  status: string
  submitted_at?: string
  approved_at?: string
  approved_by?: number
  rejection_reason?: string
  rejected_at?: string
  rejected_by?: number
  bill_attachments?: string[]
  created_at: string
  updated_at: string
  project_site?: {
    id: number
    site_id: string
    site_name: string
  }
  staff: {
    id: number
    first_name: string
    last_name: string
    association_type: string
  }
  rejector?: {
    id: number
    first_name: string
    last_name: string
    association_type?: string
  }
  // Newly Added
  approver?: {
    id: number
    first_name: string
    last_name: string
    association_type?: string
  }
  reporting_person?: {
  id: number
  first_name: string
  last_name: string
  association_type?: string
}
}

interface PaginationType {
  total_count: number
  page: number
  limit: number
  totalPages: number
}

export interface GeneralClaimFilters {
  search?: string
  staff_id?: string
  staff_name?: string
  approved_by?: string
  approved_by_name?: string
  status?: string
  /** Comma-separated statuses, for links that count more than one. */
  status_list?: string
  claim_type?: string
  title?: string
  description?: string
  staff_type?: string
  state_id?: string
  district_id?: string
  min_submitted_amount?: string
  max_submitted_amount?: string
  min_approved_amount?: string
  max_approved_amount?: string
  claim_date_from?: string
  claim_date_to?: string
  created_from?: string
  created_to?: string
  is_approved?: string
  has_attachments?: string
  sortBy?: string
  sortOrder?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: GeneralClaimFilters
  onFiltersChange: (filters: GeneralClaimFilters) => void
  onApply: () => void
  onReset: () => void
}

const GeneralClaimFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const [activeTab, setActiveTab] = useState('basic')
  const [selectedStaff, setSelectedStaff] = useState<SelectOption | null>(
    filters.staff_id
      ? {
        id: Number(filters.staff_id),
        value: Number(filters.staff_id),
        label: filters.staff_name || `Staff ID: ${filters.staff_id}`
      }
      : null
  )
  const [selectedApprover, setSelectedApprover] = useState<SelectOption | null>(
    filters.approved_by
      ? {
        id: Number(filters.approved_by),
        value: Number(filters.approved_by),
        label: filters.approved_by_name || `Approver ID: ${filters.approved_by}`
      }
      : null
  )

  useEffect(() => {
    if (filters.staff_id) {
      setSelectedStaff({
        id: Number(filters.staff_id),
        value: Number(filters.staff_id),
        label: filters.staff_name || `Staff ID: ${filters.staff_id}`
      })
    } else {
      setSelectedStaff(null)
    }
  }, [filters.staff_id, filters.staff_name])

  useEffect(() => {
    if (filters.approved_by) {
      setSelectedApprover({
        id: Number(filters.approved_by),
        value: Number(filters.approved_by),
        label: filters.approved_by_name || `Approver ID: ${filters.approved_by}`
      })
    } else {
      setSelectedApprover(null)
    }
  }, [filters.approved_by, filters.approved_by_name])

  if (!isOpen) return null

  const handleInputChange = (field: keyof GeneralClaimFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    })
  }

  const handleStaffSelect = (option: SelectOption | null) => {
    setSelectedStaff(option)
    onFiltersChange({
      ...filters,
      staff_id: option ? String(option.value) : undefined,
      staff_name: option ? option.label : undefined
    })
  }

  const handleApproverSelect = (option: SelectOption | null) => {
    setSelectedApprover(option)
    onFiltersChange({
      ...filters,
      approved_by: option ? String(option.value) : undefined,
      approved_by_name: option ? option.label : undefined
    })
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    // { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'amount', label: 'Amounts', icon: FaRupeeSign },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'approval', label: 'Approval', icon: FiCheckSquare },
    { id: 'other', label: 'Other', icon: FiUser }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search claims..."
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Claim Type</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Claim Type"
                  value={filters.claim_type || ''}
                  onChange={(e) => handleInputChange('claim_type', e.target.value)}
                />
              </div> */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Staff Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.staff_type || ''}
                  onChange={(e) => handleInputChange('staff_type', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="field">Field</option>
                  <option value="office">Office</option>
                </select>
              </div> */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Title"
                  value={filters.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Description"
                  value={filters.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div> */}
            </div>
          )}

          {/* Location Tab */}
          {/* {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State ID"
                  value={filters.state_id || ''}
                  onChange={(e) => handleInputChange('state_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  placeholder="District ID"
                  value={filters.district_id || ''}
                  onChange={(e) => handleInputChange('district_id', e.target.value)}
                />
              </div>
            </div>
          )} */}

          {/* Amounts Tab */}
          {activeTab === 'amount' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Submitted Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Min Submitted Amount"
                  value={filters.min_submitted_amount || ''}
                  onChange={(e) => handleInputChange('min_submitted_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Submitted Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Max Submitted Amount"
                  value={filters.max_submitted_amount || ''}
                  onChange={(e) => handleInputChange('max_submitted_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Approved Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  placeholder="Min Approved Amount"
                  value={filters.min_approved_amount || ''}
                  onChange={(e) => handleInputChange('min_approved_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Approved Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  placeholder="Max Approved Amount"
                  value={filters.max_approved_amount || ''}
                  onChange={(e) => handleInputChange('max_approved_amount', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Claim Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.claim_date_from || ''}
                  onChange={(e) => handleInputChange('claim_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Claim Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.claim_date_to || ''}
                  onChange={(e) => handleInputChange('claim_date_to', e.target.value)}
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div> */}
            </div>
          )}

          {/* Approval Tab */}
          {activeTab === 'approval' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved By</label>
                <SelectWithSearch2
                  placeholder="Search approver"
                  value={selectedApprover}
                  onChange={handleApproverSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`
                  })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Approved</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.is_approved || ''}
                  onChange={(e) => handleInputChange('is_approved', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}

          {/* Other Tab */}
          {activeTab === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Staff</label>
                <SelectWithSearch2
                  placeholder="Search staff"
                  value={selectedStaff}
                  onChange={handleStaffSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`
                  })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Attachments</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_attachments || ''}
                  onChange={(e) => handleInputChange('has_attachments', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply()
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
interface GeneralClaimsListProps {
  claims: GeneralClaim[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onFilterChange: (filter: string) => void
  statusFilter: string
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdvancedFilter?: (filters: GeneralClaimFilters) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  appliedFilters?: GeneralClaimFilters
  searchValue?: string
  onRefresh?: () => void
}

const EMPTY_SUBMIT_FORM = {
  claim_title: "",
  claim_description: "",
  travel_amount: "",
  da_amount: "",
  stay_amount: "",
  food_amount: "",
  other_amount1: "",
  other_description1: "",
  other_amount2: "",
  other_description2: "",
}

export const GeneralClaimsList: React.FC<GeneralClaimsListProps> = ({
  claims,
  pagination,
  onSearch,
  onFilterChange,
  statusFilter,
  onPageChange,
  onLimitChange,
  onAdvancedFilter,
  loading = false,
  sortConfig,
  onSort,
  appliedFilters,
  searchValue = "",
  onRefresh,
}) => {
  const { hasPermission } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [filters, setFilters] = useState<GeneralClaimFilters>(useUrlFilters<GeneralClaimFilters>({}, URL_FILTER_KEYS))
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])

  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submitForm, setSubmitForm] = useState(EMPTY_SUBMIT_FORM)
  const [submitFiles, setSubmitFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSubmitClaim = async () => {
    if (!submitForm.claim_title.trim()) {
      toast.error("Claim title is required")
      return
    }
    if (submitForm.claim_description.trim().length < 10) {
      toast.error("Description must be at least 10 characters")
      return
    }
    try {
      setSubmitting(true)
      const formData = new FormData()
      formData.append("claim_title", submitForm.claim_title.trim())
      formData.append("claim_description", submitForm.claim_description.trim())
      if (submitForm.travel_amount) formData.append("travel_amount", submitForm.travel_amount)
      if (submitForm.da_amount) formData.append("da_amount", submitForm.da_amount)
      if (submitForm.stay_amount) formData.append("stay_amount", submitForm.stay_amount)
      if (submitForm.food_amount) formData.append("food_amount", submitForm.food_amount)
      if (submitForm.other_amount1) formData.append("other_amount1", submitForm.other_amount1)
      if (submitForm.other_description1) formData.append("other_description1", submitForm.other_description1.trim())
      if (submitForm.other_amount2) formData.append("other_amount2", submitForm.other_amount2)
      if (submitForm.other_description2) formData.append("other_description2", submitForm.other_description2.trim())
      submitFiles.forEach((file) => formData.append("bills", file))

      await axiosInstance.post("/claims/general/submit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      toast.success("Claim submitted successfully")
      setShowSubmitModal(false)
      setSubmitForm(EMPTY_SUBMIT_FORM)
      setSubmitFiles([])
      onRefresh?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to submit claim")
    } finally {
      setSubmitting(false)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange(e.target.value)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
  }

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof GeneralClaimFilters]).length
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return "₹0"
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { icon: FaClock, bgColor: "bg-gray-100", textColor: "text-gray-800", label: "Pending" },
      submitted: { icon: FaClock, bgColor: "bg-blue-100", textColor: "text-blue-800", label: "Submitted" },
      approved: { icon: FaCheckCircle, bgColor: "bg-green-100", textColor: "text-green-800", label: "Approved" },
      rejected: { icon: FaTimesCircle, bgColor: "bg-red-100", textColor: "text-red-800", label: "Rejected" },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending
    const IconComponent = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <IconComponent className="w-2 h-2" />
        {config.label}
      </span>
    )
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction =
      sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  const getAppliedFilters = () => appliedFilters ?? {}

  const getSearchParam = () => (searchValue ?? "").trim() || searchTerm.trim()

  const mapClaimForExport = (claim: GeneralClaim) => {
    const staffName = [claim.staff?.first_name, claim.staff?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim()
    const rejectorName = [claim.rejector?.first_name, claim.rejector?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim()
    const approverName = [claim.approver?.first_name, claim.approver?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim()

    const formatValue = (value?: string) => (value ? formatDate(value) : "")

    return {
      // "Claim ID": claim.id,
      "Claim Title": claim.claim_title,
      "Claim Description": claim.claim_description,
      // "Claim Category": claim.claim_category || "",
      "Claim Date": formatDate(claim.claim_date),
      "Staff": staffName || claim.staff_id,
      // "Staff Type": claim.staff_type || "",
      "Staff Association": claim.staff?.association_type || claim.association_type || "",
      "Travel Amount": claim.travel_amount ?? 0,
      "DA Amount": claim.da_amount ?? 0,
      "Stay Amount": claim.stay_amount ?? 0,
      "Food Amount": claim.food_amount ?? 0,
      "Other Amount 1": claim.other_amount1 ?? 0,
      "Other Description 1": claim.other_description1 || "",
      "Other Amount 2": claim.other_amount2 ?? 0,
      "Other Description 2": claim.other_description2 || "",
      "Submitted Total": claim.submitted_total ?? 0,
      "Approved Travel Amount": claim.approved_travel_amount ?? 0,
      "Approved DA Amount": claim.approved_da_amount ?? 0,
      "Approved Stay Amount": claim.approved_stay_amount ?? 0,
      "Approved Food Amount": claim.approved_food_amount ?? 0,
      "Approved Other Amount 1": claim.approved_other_amount1 ?? 0,
      "Approved Other Description 1": claim.approved_other_description1 || "",
      "Approved Other Amount 2": claim.approved_other_amount2 ?? 0,
      "Approved Other Description 2": claim.approved_other_description2 || "",
      "Final Approved Total": claim.final_approved_total ?? 0,
      Status: claim.status,
      "Submitted At": formatValue(claim.submitted_at),
      "Approved By": approverName || claim.approved_by || "",
      "Approved At": formatValue(claim.approved_at),
      "Rejection Reason": claim.rejection_reason || "",
      "Rejected By": rejectorName || claim.rejected_by || "",
      "Rejected At": formatValue(claim.rejected_at),
      "Bill Attachments": claim.bill_attachments?.join("; ") || "",
      // "Project Site ID": claim.project_site?.site_id || "",
      // "Project Site Name": claim.project_site?.site_name || "",
      "Created At": formatDate(claim.created_at),
      "Updated At": formatDate(claim.updated_at),
    }
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const baseParams: Record<string, any> = {
        ...getAppliedFilters(),
      }

      const searchParam = getSearchParam()
      if (searchParam) baseParams.search = searchParam
      if (statusFilter) baseParams.status = statusFilter

      if (sortConfig?.field) {
        baseParams.sort_by = sortConfig.field
        baseParams.sort_order = sortConfig.direction
      }

      Object.keys(baseParams).forEach((key) => {
        if (baseParams[key] === undefined || baseParams[key] === null || baseParams[key] === "") {
          delete baseParams[key]
        }
      })

      const allClaims: GeneralClaim[] = []
      let currentPage = 1
      let totalPages = 1
      const pageSize = Number(pagination?.limit) || 10

      do {
        const params = {
          ...baseParams,
          page: currentPage,
          limit: pageSize,
        }

        const response = await axiosInstance.get("/claims/general/list", { params })
        if (!response.data.success) {
          break
        }

        const responseClaims: GeneralClaim[] = response.data.data.claims || []
        allClaims.push(...responseClaims)

        const paginationInfo = response.data.data.pagination || {}
        totalPages =
          paginationInfo.total_pages ??
          paginationInfo.totalPages ??
          paginationInfo.total_pages ??
          paginationInfo.total_pages ??
          currentPage

        currentPage += 1
      } while (currentPage <= totalPages)

      if (allClaims.length) {
        setExportData(allClaims.map(mapClaimForExport))
        setShowExcelModal(true)
      } else {
        setExportData([])
        setShowExcelModal(true)
      }
    } catch (error) {
      console.error("Error exporting general claims:", error)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">General Claims</h2>
            <p className="text-sm text-gray-600 mt-1">Manage field and office staff claims</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search claims..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
              />
              <FiSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            </div>

            <button
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded transition-colors text-sm font-medium ${getActiveFiltersCount() > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>

            <select
              value={statusFilter}
              onChange={handleFilterChange}
              className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export"}
            </button>

            <button
              onClick={() => setShowSubmitModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium"
            >
              <FiPlus className="w-4 h-4" />
              Submit Claim
            </button>
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Submit General Claim</h3>
              <button onClick={() => { setShowSubmitModal(false); setSubmitForm(EMPTY_SUBMIT_FORM); setSubmitFiles([]) }} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  maxLength={200}
                  value={submitForm.claim_title}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, claim_title: e.target.value }))}
                  placeholder="Enter claim title"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  value={submitForm.claim_description}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, claim_description: e.target.value }))}
                  placeholder="Describe the claim (min 10 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "travel_amount", label: "Travel Amount" },
                  { key: "da_amount", label: "DA Amount" },
                  { key: "stay_amount", label: "Stay Amount" },
                  { key: "food_amount", label: "Food Amount" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label} (₹)</label>
                    <input
                      type="number"
                      min={0}
                      value={(submitForm as any)[key]}
                      onChange={(e) => setSubmitForm((p) => ({ ...p, [key]: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Amount 1 (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={submitForm.other_amount1}
                    onChange={(e) => setSubmitForm((p) => ({ ...p, other_amount1: e.target.value }))}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description 1</label>
                  <input
                    type="text"
                    maxLength={200}
                    value={submitForm.other_description1}
                    onChange={(e) => setSubmitForm((p) => ({ ...p, other_description1: e.target.value }))}
                    placeholder="Describe other expense"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Amount 2 (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={submitForm.other_amount2}
                    onChange={(e) => setSubmitForm((p) => ({ ...p, other_amount2: e.target.value }))}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description 2</label>
                  <input
                    type="text"
                    maxLength={200}
                    value={submitForm.other_description2}
                    onChange={(e) => setSubmitForm((p) => ({ ...p, other_description2: e.target.value }))}
                    placeholder="Describe other expense"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bill Attachments (up to 10)</label>
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 text-sm text-gray-600">
                  <FiUpload className="w-4 h-4" />
                  <span>Choose files</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || [])
                      setSubmitFiles((prev) => [...prev, ...selected].slice(0, 10))
                      e.target.value = ""
                    }}
                  />
                </label>
                {submitFiles.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {submitFiles.map((f, idx) => (
                      <li key={idx} className="flex items-center justify-between text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                        <span className="truncate max-w-xs">{f.name}</span>
                        <button onClick={() => setSubmitFiles((prev) => prev.filter((_, i) => i !== idx))} className="ml-2 text-red-500 hover:text-red-700">
                          <FiTrash2 className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => { setShowSubmitModal(false); setSubmitForm(EMPTY_SUBMIT_FORM); setSubmitFiles([]) }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitClaim}
                disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-400"
              >
                {submitting ? "Submitting..." : "Submit Claim"}
              </button>
            </div>
          </div>
        </div>
      )}

      <GeneralClaimFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Stats */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Claims</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total_count}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaCalendarAlt className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {claims.filter(c => c.status === 'submitted').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaClock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {claims.filter(c => c.status === 'approved').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
              <FaCheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {claims.filter(c => c.status === 'rejected').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded flex items-center justify-center">
              <FaTimesCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div> */}

      {/* Table */}
      <div className="bg-white rounded shadow-sm border border-gray-200 ooverflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("claim_title")}
                  >
                    <span>Claim Title</span>
                    {getSortIcon("claim_title")}
                  </button>
                </th>

                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("claim_date")}
                  >
                    <span>Claim Date</span>
                    {getSortIcon("claim_date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>

                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reporting Person
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("submitted_at")}
                  >
                    <span>Date of Submission</span>
                    {getSortIcon("submitted_at")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("approved_at")}
                  >
                    <span>Date of Approval</span>
                    {getSortIcon("approved_at")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approved By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Association
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("submitted_total")}
                  >
                    <span>Submitted Amount</span>
                    {getSortIcon("submitted_total")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("final_approved_total")}
                  >
                    <span>Approved Amount</span>
                    {getSortIcon("final_approved_total")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("status")}
                  >
                    <span>Claim Status</span>
                    {getSortIcon("status")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim Reason
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 text-sm">Loading claims...</p>
                    </div>
                  </td>
                </tr>
              ) : claims.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <FaExclamationCircle className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-sm">No claims found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                claims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {hasPermission('general_claim', 'view') && (
                          <Link
                            to={`/claims/general/${claim.id}`}
                            className="flex justify-center items-center w-fit h-fit text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          >
                            <LuEye className="w-4 h-4" />
                          </Link>
                        )}
                        {hasPermission('general_claim', 'submit') && claim.status === 'submitted' && (
                          <Link
                            to={`/claims/general/${claim.id}?edit=true`}
                            className="flex justify-center items-center w-fit h-fit text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded transition-colors"
                            title="Edit submitted claim"
                          >
                            <FiEdit className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col max-w-[250px]">
                        <span className="text-sm font-semibold text-gray-900 truncate" title={claim.claim_title}>
                          {claim.claim_title}
                        </span>
                        <span className="text-xs text-gray-600 truncate" title={claim.claim_description}>
                          {claim.claim_description}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-900">{formatDate(claim.claim_date)}</span>
                      </div>
                    </td>
                    {/* User Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {claim.staff
                          ? `${claim.staff.first_name} ${claim.staff.last_name}`
                          : "N/A"}
                      </span>
                    </td>

                    {/* Project Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {claim.project_site?.site_name || "N/A"}
                      </span>
                    </td>

                    {/* Reporting Person */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {claim.reporting_person
                          ? `${claim.reporting_person.first_name} ${claim.reporting_person.last_name}`
                          : "N/A"}
                      </span>
                    </td>

                    {/* Date of Submission */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {claim.submitted_at ? formatDate(claim.submitted_at) : "N/A"}
                      </span>
                    </td>

                    {/* Date of Approval */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {claim.approved_at ? formatDate(claim.approved_at) : "N/A"}
                      </span>
                    </td>

                    {/* Approved By */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {claim.approver
                          ? `${claim.approver.first_name} ${claim.approver.last_name}`
                          : "N/A"}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim.staff.association_type === "inhouse"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-orange-100 text-orange-800"
                          }`}
                      >
                        {claim.staff.association_type === "inhouse" ? "Onrole" : "Offrole"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(claim.submitted_total)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {claim.final_approved_total ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-green-900">
                            {formatCurrency(claim.final_approved_total)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(claim.status)}</td>
                    
                    {/* Claim Reason */}
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 max-w-[200px] truncate inline-block" title={claim.rejection_reason || ""}>
                        {claim.rejection_reason || "N/A"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="general_claims"
        title="Export General Claims Data"
        description="Download the full set of claim columns"
      />
    </div>
  )
}
